import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { PieChart, Pie, Sector, ResponsiveContainer } from 'recharts';
import { Button } from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { getCRMPipelineStats } from '../../api';

const TRACK_COLOR = '#E8E8E8';
const OTHER_COLOR = '#9CA3AF';

const STAGE_ORDER = ['won', 'negotiat', 'lost', 'contacted', 'new'];
const STAGE_COLOR_BY_NAME = [
  { match: ['won'], color: '#50AB3D' },
  { match: ['negotiat'], color: '#CB30E0' },
  { match: ['lost'], color: '#FF383C' },
  { match: ['contacted', 'contact'], color: '#EEB90B' },
  { match: ['new lead', 'new leads', 'new'], color: '#0088FF' },
];

const SIXTH_SLOT_COLOR = '#FF610E';

const MAX_VISIBLE_STAGES = 6;

function normalizeName(name = '') {
  return String(name).trim().toLowerCase();
}

function getStageColor(stage, index) {
  const key = normalizeName(stage?.stage_name);
  for (const entry of STAGE_COLOR_BY_NAME) {
    if (entry.match.some((m) => key.includes(m))) return entry.color;
  }
  if (index === STAGE_COLOR_BY_NAME.length) return SIXTH_SLOT_COLOR;
  return OTHER_COLOR;
}

function lightenColor(hex, percent) {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.min(
    255,
    Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * percent),
  );
  const g = Math.min(
    255,
    Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * percent),
  );
  const b = Math.min(
    255,
    Math.round((num & 0xff) + (255 - (num & 0xff)) * percent),
  );
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function formatLeadCount(count) {
  return String(Number(count) || 0).padStart(2, '0');
}

function sortStages(stages) {
  return [...stages].sort((a, b) => {
    const aKey = normalizeName(a.stage_name);
    const bKey = normalizeName(b.stage_name);
    const aIdx = STAGE_ORDER.findIndex((key) => aKey.includes(key));
    const bIdx = STAGE_ORDER.findIndex((key) => bKey.includes(key));
    return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
  });
}

function buildSlices(stages) {
  return stages.map((stage, index) => ({
    id: stage.stage_name,
    label: stage.stage_name,
    value: Number(stage.contact_count) || 0,
    color: getStageColor(stage, index),
  }));
}

function makePieGradientShape(hoveredId) {
  return function PieGradientShape(props) {
    const { payload, isActive } = props;
    const color = payload?.color || TRACK_COLOR;
    const dimmed = hoveredId && hoveredId !== payload?.id;

    return (
      <g
        opacity={dimmed ? 0.35 : 1}
        className="transition-opacity duration-150 cursor-pointer"
      >
        <Sector
          {...props}
          fill={lightenColor(color, 0.55)}
          stroke={color}
          strokeWidth={isActive ? 3 : 2}
        />
      </g>
    );
  };
}

function DonutChart({ slices, total, size = 200, hoveredId, onHoverSlice }) {
  const chartData = slices.filter((s) => s.value > 0);
  // No leads yet — draw one full gray ring so the card isn't blank.
  const pieData = chartData.length
    ? chartData
    : [{ id: 'empty', label: '', value: 1, color: TRACK_COLOR }];
  const gradientShape = useMemo(
    () => makePieGradientShape(hoveredId),
    [hoveredId],
  );

  return (
    <div
      className="relative mx-auto aspect-square max-w-full min-w-0"
      style={{ width: size }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="label"
            innerRadius="32%"
            outerRadius="100%"
            startAngle={90}
            endAngle={-270}
            shape={gradientShape}
            isAnimationActive={false}
            onMouseEnter={(entry) => onHoverSlice?.(entry.id)}
            onMouseLeave={() => onHoverSlice?.(null)}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-[28px] sm:text-[32px] leading-none font-bold text-[#1F2241] font-sf-pro-display">
          {total}
        </span>
        <span className="text-[12px] text-[#868686] font-medium mt-1.5">
          Total Leads
        </span>
      </div>
    </div>
  );
}

function LegendRow({ slice, total, isActive, onHover }) {
  const percent = total > 0 ? Math.round((slice.value / total) * 100) : 0;
  return (
    <div
      onMouseEnter={() => onHover(slice.id)}
      onMouseLeave={() => onHover(null)}
      className={`relative flex items-center justify-between gap-3 pl-4 pr-4 py-3.5 rounded-[12px] overflow-hidden cursor-default transition-colors ${
        isActive
          ? 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]'
          : 'bg-[#F5F5F5] hover:bg-[#EFEFEF]'
      }`}
    >
      <span
        className="absolute left-0 top-2.5 bottom-2.5 w-[4px] rounded-r-full"
        style={{ backgroundColor: slice.color }}
      />
      <span className="text-[15px] font-medium text-[#1F2241] truncate">
        {slice.label}
      </span>
      <div className="flex items-center gap-5 shrink-0">
        <span className="text-[13px] text-[#868686] tabular-nums">
          {formatLeadCount(slice.value)} Leads
        </span>
        <span className="text-[14px] font-semibold text-[#1F2241] w-10 text-right tabular-nums">
          {percent}%
        </span>
      </div>
    </div>
  );
}

export function SalesCategoryChart() {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);
  const [donutSize, setDonutSize] = useState(340);

  useEffect(() => {
    const media = window.matchMedia(
      '(min-width: 1024px) and (max-width: 1440px) and (max-height: 899px)',
    );
    const apply = () => setDonutSize(media.matches ? 220 : 340);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  const contentRef = useRef(null);
  const [canFitSideBySide, setCanFitSideBySide] = useState(false);
  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      setCanFitSideBySide(entry.contentRect.width >= 640);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const {
    data,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['crm-pipeline-stats', companyId],
    queryFn: () => getCRMPipelineStats(companyId),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && data !== undefined,
    300,
    500,
  );
  const showSkeleton = isInitialLoading || showDelayedSkeleton;

  useEffect(() => {
    if (isError) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load pipeline stages from the server.',
      );
    }
  }, [isError, error]);

  const { visibleSlices, hasMoreStages, totalContacts } = useMemo(() => {
    const stages = sortStages(data?.stages || []);
    const slices = buildSlices(stages);
    // Highest lead count first; ties keep the identity-order above stable.
    const ranked = [...slices].sort((a, b) => b.value - a.value);
    return {
      visibleSlices: ranked.slice(0, MAX_VISIBLE_STAGES),
      hasMoreStages: ranked.length > MAX_VISIBLE_STAGES,
      totalContacts: data?.total_contacts ?? 0,
    };
  }, [data]);

  if (showSkeleton || !data) {
    return (
      <div className="bg-white rounded-[20px] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5 sm:p-6 h-full min-h-[340px] animate-pulse">
        <div className="h-5 w-36 bg-skeleton-base rounded mb-5" />
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8 sm:gap-10 pt-2">
          <div className="w-[340px] h-[340px] rounded-full bg-skeleton-base shrink-0" />
          <div className="w-full flex-1 flex flex-col gap-2.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-[54px] bg-skeleton-base rounded-[12px]"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const hasStages = (data?.stages || []).length > 0;

  return (
    <div className="bg-white rounded-[20px] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-5 sm:p-6 h-full flex flex-col font-sf-pro-display">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h3 className="text-[18px] leading-7 font-semibold text-[#1F2241]">
          Lead Inquiries
        </h3>
        {hasMoreStages && (
          <Button
            variant="primary"
            className="h-9 px-3.5 text-sm shrink-0"
            onClick={() => navigate('/crm-pipeline')}
          >
            View all
            <span aria-hidden>→</span>
          </Button>
        )}
      </div>

      {!hasStages ? (
        <div className="flex-1 flex items-center justify-center text-text-secondary text-sm py-10">
          No pipeline stages available
        </div>
      ) : (
        <div
          ref={contentRef}
          className="flex-1 min-h-0 flex flex-wrap items-center gap-8 sm:gap-10 pt-2"
        >
          <DonutChart
            slices={visibleSlices}
            total={totalContacts}
            size={donutSize}
            hoveredId={hoveredId}
            onHoverSlice={setHoveredId}
          />
          <div
            className={`w-full min-w-[240px] flex-1 h-full overflow-y-auto custom-scrollbar flex flex-col gap-2.5 ${canFitSideBySide ? 'max-w-[380px]' : ''}`}
          >
            {visibleSlices.map((slice) => (
              <LegendRow
                key={slice.id}
                slice={slice}
                total={totalContacts}
                isActive={hoveredId === slice.id}
                onHover={setHoveredId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
